import { useEffect, useState, type FormEvent } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@project/components/ui/card";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import {
  paymentSettingsService,
  type PaymentSettingsPayload,
  type PaymentSettingsRecord,
} from "@project/services/admin/payment-settings.service";
import { Save } from "lucide-react";

type PaymentSettingsForm = PaymentSettingsPayload & {
  id?: number | string;
};
type PaymentSettingsField = keyof PaymentSettingsPayload;

const emptySettings: PaymentSettingsForm = {
  upi_id: "",
  qr_code_url: "",
  account_name: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  branch: "",
  pan_number: "",
  registration_80g: "",
  trust_registration_number: "",
  trust_address: "",
  contact_phone: "",
  contact_email: "",
};

function toForm(record?: PaymentSettingsRecord): PaymentSettingsForm {
  return {
    ...emptySettings,
    ...record,
  };
}

function toPayload(data: PaymentSettingsForm): PaymentSettingsPayload {
  return {
    upi_id: data.upi_id.trim(),
    qr_code_url: data.qr_code_url.trim(),
    account_name: data.account_name.trim(),
    bank_name: data.bank_name.trim(),
    account_number: data.account_number.trim(),
    ifsc_code: data.ifsc_code.trim(),
    branch: data.branch.trim(),
    pan_number: data.pan_number.trim(),
    registration_80g: data.registration_80g.trim(),
    trust_registration_number: data.trust_registration_number.trim(),
    trust_address: data.trust_address.trim(),
    contact_phone: data.contact_phone.trim(),
    contact_email: data.contact_email.trim(),
  };
}

function buildChangedPayload(
  original: PaymentSettingsPayload,
  current: PaymentSettingsPayload
): Partial<PaymentSettingsPayload> {
  const patch: Partial<PaymentSettingsPayload> = {};
  (Object.keys(current) as PaymentSettingsField[]).forEach(key => {
    if (current[key] !== original[key]) {
      patch[key] = current[key];
    }
  });
  return patch;
}

function getErrorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

export default function Page() {
  const [data, setData] = useState<PaymentSettingsForm>(emptySettings);
  const [originalPayload, setOriginalPayload] =
    useState<PaymentSettingsPayload | null>(null);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const settings = await paymentSettingsService.get();
        if (settings) {
          const form = toForm(settings);
          setData(form);
          setOriginalPayload(toPayload(form));
          setSettingsSaved(Boolean(settings.id));
        }
      } catch (error) {
        if (getErrorStatus(error) !== 404) {
          projectToast.error(
            getApiErrorMessage(error, "Failed to load payment settings.")
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(data);
      let saved: PaymentSettingsRecord;

      if (settingsSaved) {
        if (!originalPayload) {
          projectToast.error("Original settings not loaded. Please refresh.");
          return;
        }
        if (!data.id) {
          projectToast.error("Settings ID missing. Please refresh.");
          return;
        }
        const patch = buildChangedPayload(originalPayload, payload);
        if (Object.keys(patch).length === 0) {
          projectToast.info("No changes to update.");
          return;
        }
        try {
          saved = await paymentSettingsService.update(data.id, patch);
        } catch (error) {
          if (getErrorStatus(error) !== 404) {
            throw error;
          }
          saved = await paymentSettingsService.create(payload);
        }
      } else {
        saved = await paymentSettingsService.create(payload);
      }

      const nextForm = toForm(saved);
      setData(nextForm);
      setOriginalPayload(toPayload(nextForm));
      setSettingsSaved(true);
      projectToast.success("Payment settings saved.");
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Failed to save payment settings.")
      );
    } finally {
      setSaving(false);
    }
  };

  const setField = (k: PaymentSettingsField, v: string) =>
    setData(p => ({ ...p, [k]: v }));
  const Field = ({
    label,
    k,
    placeholder,
    type = "text",
  }: {
    label: string;
    k: PaymentSettingsField;
    placeholder?: string;
    type?: string;
  }) => (
    <div>
      <Label>{label}</Label>
      <Input
        type={type}
        value={data[k]}
        onChange={e => setField(k, e.target.value)}
        placeholder={placeholder}
        className="bg-muted/40 rounded-xl border-transparent"
      />
    </div>
  );

  return (
    <AdminShell
      title="Payment Settings"
      subtitle="UPI, bank details, 80G info shown on the donation page"
    >
      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <form onSubmit={save} className="max-w-4xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>UPI & QR</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="UPI ID" k="upi_id" placeholder="jivandhara@upi" />
              <Field
                label="QR Code Image URL"
                k="qr_code_url"
                placeholder="https://..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bank Account</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Account Name" k="account_name" />
              <Field label="Bank Name" k="bank_name" />
              <Field label="Account Number" k="account_number" />
              <Field label="IFSC Code" k="ifsc_code" />
              <Field label="Branch" k="branch" />
              <Field label="PAN of Trust" k="pan_number" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Trust & 80G Registration</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="80G Registration Number" k="registration_80g" />
              <Field
                label="Trust Registration Number"
                k="trust_registration_number"
              />
              <div className="sm:col-span-2">
                <Label>Trust Address</Label>
                <Textarea
                  value={data.trust_address ?? ""}
                  onChange={e => setField("trust_address", e.target.value)}
                  rows={3}
                  className="bg-muted/40 rounded-xl border-transparent"
                />
              </div>
              <Field label="Contact Phone" k="contact_phone" />
              <Field label="Contact Email" k="contact_email" type="email" />
            </CardContent>
          </Card>

          <div className="sticky bottom-0 -mx-3 border-t border-border bg-card p-3 sm:mx-0">
            <Button
              type="submit"
              disabled={saving}
              className="gap-1 rounded-xl"
            >
              <Save className="h-4 w-4" />{" "}
              {saving
                ? "Saving…"
                : settingsSaved
                  ? "Update Settings"
                  : "Create Settings"}
            </Button>
          </div>
        </form>
      )}
    </AdminShell>
  );
}
